<!-- Quality Gate Header -->
<div class="dashboard__header">
  <div class="quality-gate"
       [class.quality-gate--passed]="qualityGatePassed"
       [class.quality-gate--failed]="!qualityGatePassed">
    <ion-icon [name]="qualityGatePassed ? 'checkmark-circle' : 'close-circle'"></ion-icon>
    <span>{{ 'ANALYSIS.QUALITY_GATE' | translate }}:
      {{ qualityGatePassed ? ('ANALYSIS.PASSED' | translate) : ('ANALYSIS.FAILED' | translate) }}
    </span>
  </div>
  <span class="dashboard__meta">
    {{ report.projectName }} · {{ report.totalFilesAnalyzed }} {{ 'ANALYSIS.FILES_ANALYZED' | translate }}
  </span>
  <div class="dashboard__export-buttons">
    <button class="export-btn" (click)="exportToPdf()" [disabled]="isExporting">
      <ion-icon name="document-outline"></ion-icon>
      {{ 'ANALYSIS.EXPORT_PDF' | translate }}
    </button>
    <button class="export-btn" (click)="exportToHtml()" [disabled]="isExporting">
      <ion-icon name="code-slash-outline"></ion-icon>
      {{ 'ANALYSIS.EXPORT_HTML' | translate }}
    </button>
    <button class="export-btn" (click)="exportToExcel()" [disabled]="isExporting">
      <ion-icon name="grid-outline"></ion-icon>
      {{ 'ANALYSIS.EXPORT_EXCEL' | translate }}
    </button>
  </div>
</div>

<!-- Score Cards Row -->
<div class="dashboard__scores">
  <!-- Card: Clean Code -->
  <div class="score-card" *ngIf="showCleanCodeCard">
    <h4 class="score-card__title">{{ 'ANALYSIS.CLEAN_CODE' | translate }}</h4>
    <div class="score-card__value">
      <span class="score-card__number">{{ formatScore(cleanCodeScore) }}</span>
      <span class="score-card__max">/10</span>
      <span class="score-card__rating" [ngClass]="getRatingClass(cleanCodeScore)">
        {{ getRating(cleanCodeScore) }}
      </span>
    </div>
    <div class="score-card__bar">
      <div class="score-card__bar-fill" [style.width.%]="cleanCodeScore * 10"></div>
    </div>
    <div class="score-card__breakdown">
      <span class="severity severity--critical">{{ report.summary.criticalViolations }} {{ 'ANALYSIS.CRITICAL' | translate }}</span>
      <span class="severity severity--major">{{ report.summary.majorViolations }} {{ 'ANALYSIS.MAJOR' | translate }}</span>
      <span class="severity severity--minor">{{ report.summary.minorViolations }} {{ 'ANALYSIS.MINOR' | translate }}</span>
      <span class="severity severity--info">{{ report.summary.infoViolations }} {{ 'ANALYSIS.INFO' | translate }}</span>
    </div>
  </div>

  <!-- Card: Code Smells -->
  <div class="score-card" *ngIf="showCodeSmellsCard">
    <h4 class="score-card__title">{{ 'ANALYSIS.CODE_SMELLS' | translate }}</h4>
    <div class="score-card__value">
      <span class="score-card__number">{{ formatScore(codeSmellsScore) }}</span>
      <span class="score-card__max">/10</span>
      <span class="score-card__rating" [ngClass]="getRatingClass(codeSmellsScore)">
        {{ getRating(codeSmellsScore) }}
      </span>
    </div>
    <div class="score-card__bar">
      <div class="score-card__bar-fill" [style.width.%]="codeSmellsScore * 10"></div>
    </div>
    <div class="score-card__breakdown">
      <span class="severity severity--critical">{{ report.summary.criticalSmells }} {{ 'ANALYSIS.CRITICAL' | translate }}</span>
      <span class="severity severity--major">{{ report.summary.majorSmells }} {{ 'ANALYSIS.MAJOR' | translate }}</span>
      <span class="severity severity--minor">{{ report.summary.minorSmells }} {{ 'ANALYSIS.MINOR' | translate }}</span>
      <span class="severity severity--info">{{ report.summary.infoSmells }} {{ 'ANALYSIS.INFO' | translate }}</span>
    </div>
  </div>

  <!-- Card: Security -->
  <div class="score-card" *ngIf="showSecurityCard">
    <h4 class="score-card__title">{{ 'ANALYSIS.SECURITY' | translate }}</h4>
    <div class="score-card__value">
      <span class="score-card__number">{{ formatScore(securityScore) }}</span>
      <span class="score-card__max">/10</span>
      <span class="score-card__rating" [ngClass]="getRatingClass(securityScore)">
        {{ getRating(securityScore) }}
      </span>
    </div>
    <div class="score-card__bar">
      <div class="score-card__bar-fill" [style.width.%]="securityScore * 10"></div>
    </div>
    <div class="score-card__breakdown">
      <span class="severity severity--critical">{{ report.summary.criticalVulnerabilities }} {{ 'ANALYSIS.CRITICAL' | translate }}</span>
      <span class="severity severity--high">{{ report.summary.highVulnerabilities }} {{ 'ANALYSIS.HIGH' | translate }}</span>
      <span class="severity severity--medium">{{ report.summary.mediumVulnerabilities }} {{ 'ANALYSIS.MEDIUM' | translate }}</span>
      <span class="severity severity--low">{{ report.summary.lowVulnerabilities }} {{ 'ANALYSIS.LOW' | translate }}</span>
      <span class="severity severity--info">{{ report.summary.infoVulnerabilities }} {{ 'ANALYSIS.INFO' | translate }}</span>
    </div>
  </div>
</div>

<!-- File Selector -->
<div class="dashboard__file-selector" *ngIf="report.fileReports.length > 0">
  <h4 class="dashboard__file-title">{{ 'ANALYSIS.FILE_DETAIL' | translate }}</h4>
  <ion-select [value]="selectedFileIndex"
    (ionChange)="selectFile($any($event).detail.value)" interface="popover">
    <ion-select-option *ngFor="let fr of report.fileReports; let i = index" [value]="i">
      {{ fr.fileName }}
    </ion-select-option>
  </ion-select>
</div>

<!-- Issues Table -->
<div class="dashboard__issues" *ngIf="selectedFileReport">
  <table class="issues-table" *ngIf="combinedIssues.length > 0; else noIssues">
    <thead>
      <tr>
        <th>ID</th>
        <th>{{ 'ANALYSIS.SEVERITY' | translate }}</th>
        <th>{{ 'ANALYSIS.TYPE' | translate }}</th>
        <th>{{ 'ANALYSIS.LINE' | translate }}</th>
        <th>{{ 'ANALYSIS.DESCRIPTION' | translate }}</th>
      </tr>
    </thead>
    <tbody>
      <tr *ngFor="let issue of combinedIssues">
        <td>{{ issue.id }}</td>
        <td><span class="severity-badge" [ngClass]="getSeverityClass(issue.severity)">{{ issue.severity }}</span></td>
        <td>{{ issue.type }}</td>
        <td>{{ issue.line ?? '-' }}</td>
        <td>{{ issue.description }}</td>
      </tr>
    </tbody>
  </table>
  <ng-template #noIssues>
    <p class="dashboard__no-issues">{{ 'ANALYSIS.NO_ISSUES' | translate }}</p>
  </ng-template>
</div>
